/**
 * Screen-scoped design tokens for **Compare versions** (`/contracts/compare`), read from the Figma frames of
 * 2026-11-03 (section 9167:949). Values that already exist in `theme/colors.ts` are taken from there (CLAUDE.md
 * §11.10); a hex in this file is one the palette does not have. Near-duplicates are not re-declared: the frames'
 * card-edge `#F3C8BF` is `signer.orange.border` (`#F0C9B8`), the strong dashed edge `#CFCBE0` is drawn with
 * `signer.border.strong`.
 */
import { error, focusRing, formField, signer, white } from "theme/colors";
import { CompareChangeType } from "./ContractCompareTypesAndValidation";

const ink = signer.ink; // #211547
const body = signer.text.secondary; // #4A4462
const sub = signer.text.meta; // #6B6584
const brand = signer.orange.main; // #dd3824
const brandText = signer.orange.dark; // #A8320C

export const compareColors = {
  ink,
  body,
  sub,
  brand,
  /**
   * The brand red as SMALL TEXT (E2E-5a). #dd3824 is 4.32:1 on #FAFAFD and 4.07:1 on #FDF1EC, short of the 4.5:1 of
   * WCAG 1.4.3; `signer.orange.dark` (= `inkfree.dark`) is 6.45:1 and 6.07:1. Fills, borders, icons and large text
   * keep `brand` (the frames' colour; 3:1 is enough for them). Jay, 2026-11-04: darker on small text on purpose,
   * a difference from the frames.
   */
  brandText,
  white,
  /** Card and input edge (`color/border` of the page chrome in the frames is the lighter `hairline`). */
  edge: formField.border, // #E3E1EC
  hairline: signer.border.light, // #EFEDF5
  /** Resting fill of the picker tabs' search box and the file strips. */
  fill: formField.bg, // #FAFAFD
  tint: signer.orange.tint, // #FDF1EC -- selected row, upload icon tile
  tintEdge: signer.orange.border,
  /**
   * Placeholder, upcoming step name and number. The frames use #8F8AA3 (about 3.3:1 on white, short of the 4.5:1 of
   * brief section 8 and WCAG 1.4.3), so this is the palette's own AA-darkened grey for the same role.
   */
  faint: signer.text.faint,
  /** The app-wide keyboard focus ring. */
  focus: focusRing,
  /**
   * The radio ring is a control's edge, so WCAG 1.4.11 asks 3:1 on white. The frames' #B9B5C9 is about 1.9:1; this is
   * the palette's own darker grey (`signer.text.faint`, 5.3:1) for the same role. UI/UX is told.
   */
  ring: signer.text.faint,
  /** Upcoming step ring, the quote's left edge. */
  stepRing: "#D4D1E1",
  /** Dashed edge of the upload area. */
  dashed: signer.border.strong,
  codeTagEdge: signer.border.strong,
  /** Chips' count pill, the search-no-match icon tile, a hovered ghost button. */
  chipFill: signer.surface.muted,
  locationFill: "#F6F5FA",
  danger: error.dark, // #B42318
  dangerFill: "#FDECEA", // `color/highbg`
  success: "#146C3A",
  successFill: "#E3F5EA",
  warning: "#8A4B00",
  warningFill: "#FFF4E0",
  warningEdge: "#F5E3B3",
  docx: "#1F4FA8",
  docxFill: "#E7EFFC",
} as const;

/** The five change types: tag fill, tag text. */
export const typeTone: Record<
  CompareChangeType | "other",
  { fill: string; color: string }
> = {
  changed: { fill: compareColors.warningFill, color: compareColors.warning },
  added: { fill: compareColors.successFill, color: compareColors.success },
  removed: { fill: compareColors.dangerFill, color: compareColors.danger },
  moved: { fill: "#ECE9FB", color: "#4B3AA8" },
  filled: { fill: "#E0F4F5", color: "#0B5F66" },
  /** An unknown change type from a newer service: a neutral tag (the card is still listed, under "All"). */
  other: { fill: compareColors.chipFill, color: compareColors.body },
};

export const compareTokens = {
  /** Breakpoints of the design notes: side by side from 900 px, a full-screen contract sheet below 600 px. */
  sheetBelow: "sm", // theme breakpoint key (599 px and down)
  sideBySideFrom: "md", // 900 px

  /** Brief section 8: a control on a phone is at least this tall and wide; the frames' own sizes apply from `sm`. */
  touchTarget: 44,

  page: {
    /** The frames' own gutters inside the app's centred page; the shared layout supplies the outer padding. */
    gap: "20px",
    title: { size: "30px", line: "36px", weight: 800, tracking: "-0.3px" },
    titlePhone: { size: "24px", line: "30px" },
    /** How narrow the title may get beside the Compare button before the button drops under it ("versions" is about 110 px). */
    titleMin: "160px",
    subtitle: { size: "15px", line: "22.5px" },
  },

  step: {
    radius: "14px",
    padding: "22px",
    paddingPhone: "16px",
    gap: "14px",
    badge: 28,
    badgeFont: "13px",
    titleSize: "17px",
    hintSize: "13px",
    columnGap: "24px",
  },

  /**
   * The old-version tabs. At 360 the strip is 294 px. With 14 px of side padding and an 8 px icon gap the two tabs
   * need 321 px (measured, Arial 14 px, the default button font); with the frame's 6 px of padding and a 6 px gap
   * they need 285 px, so both fit with their icons. The frame draws no icons at 360 and 6 px of padding; the icons
   * stay (Jay, E2E-7).
   */
  tabs: {
    height: 44,
    gap: "4px",
    paddingX: "14px",
    paddingXPhone: "6px",
    iconGap: "8px",
    iconGapPhone: "6px",
    icon: 18,
    size: "14px",
  },

  search: { height: 44, radius: "9px", paddingX: "12px", icon: 18, clear: 32 },

  hint: { size: "12.5px", line: "18.125px", icon: 16 },

  list: {
    height: 415,
    radius: "10px",
    rowMinHeight: 64,
    rowPaddingX: "14px",
    rowPaddingY: "12px",
    rowGap: "12px",
    radio: 20,
    radioChecked: "6px",
    selectedAccent: "3px",
    nameSize: "14px",
    nameLine: "18.9px",
    metaSize: "12px",
  },

  kind: {
    width: 40,
    height: 44,
    radius: "7px",
    icon: 18,
    label: "8.5px",
    small: { width: 30, height: 34, icon: 16, label: "7px" },
  },

  chip: {
    radius: "10px",
    paddingLeft: "14px",
    paddingRight: "12px",
    paddingY: "12px",
    gap: "12px",
    remove: 44,
    removeRadius: "8px",
    removeIcon: 18,
    invalidFill: "#FFF5F3",
  },

  dropzone: {
    radius: "12px",
    minHeight: 300,
    minHeightCompact: 150,
    paddingX: "20px",
    paddingY: "40px",
    gap: "8px",
    iconTile: 56,
    icon: 26,
    titleSize: "15px",
    metaSize: "13px",
  },

  /**
   * Compare, in the page header (Jay, 2026-11-04: the frames drew a bar at the bottom). The button is the theme's large
   * button (44 px, section 11.4); the frames draw 48 px with a 9 px radius.
   */
  compare: {
    icon: 20,
    messageRadius: "8px",
  },

  panel: {
    width: 720,
    radius: "16px",
    paddingX: "32px",
    paddingY: "48px",
    progressPadding: "32px",
    shadow: "0px 8px 30px 0px rgba(33,21,71,0.06)",
    gap: "12px",
    iconTile: 64,
    iconTileProgress: 52,
    icon: 30,
    titleSize: "20px",
    titleLine: "27px",
    textSize: "14px",
    textLine: "21.7px",
    textWidth: 520,
    stepHeight: 28,
    stepPaddingX: "14px",
    stepPaddingY: "12px",
    stepGap: "14px",
    stepRadius: "10px",
    barHeight: 6,
    barRadius: "3px",
  },

  button: { height: 42, icon: 18, small: 32 },

  result: {
    titleSize: "26px",
    summarySize: "15px",
    strip: { radius: "12px", paddingX: "18px", paddingY: "14px", gap: "16px", eyebrow: "11px" },
    notice: { radius: "10px", paddingX: "14px", paddingY: "10px", size: "13.5px", icon: 16, gap: "10px" },
    chip: { height: 40, radius: "20px", paddingX: "16px", gap: "8px", size: "14px", countSize: "12px" },
    legend: { size: "12px" },
    card: {
      radius: "14px",
      paddingTop: "18px",
      paddingBottom: "20px",
      paddingX: "22px",
      gap: "12px",
      listGap: "14px",
      tag: { height: 26, radius: "6px", paddingX: "10px", size: "12px", icon: 14 },
      heading: "15px",
      location: { size: "12.5px", radius: "6px" },
      sentence: { size: "16px", line: "23.2px" },
      codeTag: { size: "11.5px", radius: "6px", icon: 14 },
      quote: { size: "14px", line: "24.5px", radius: "8px", accent: "3px", paddingX: "16px", paddingY: "12px", collapsedLines: 6 },
    },
  },
} as const;

export default compareTokens;
